import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  documentId,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { CardMetadata } from '../types/card';

const COLLECTION = 'demo_portal_cards';

export async function getCardsForPages(
  cardIds: string[],
): Promise<CardMetadata[]> {
  if (cardIds.length === 0) return [];

  // Firestore 'in' queries limited to 30 items; batch if needed
  const batches: string[][] = [];
  for (let i = 0; i < cardIds.length; i += 30) {
    batches.push(cardIds.slice(i, i + 30));
  }

  const results: CardMetadata[] = [];
  for (const batch of batches) {
    const q = query(
      collection(db, COLLECTION),
      where(documentId(), 'in', batch),
    );
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((d) => {
      results.push({ id: d.id, ...d.data() } as CardMetadata);
    });
  }

  return results;
}

export async function getCard(id: string): Promise<CardMetadata | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as CardMetadata;
}

export async function createCard(
  data: Omit<CardMetadata, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCard(
  id: string,
  updates: Partial<Omit<CardMetadata, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCard(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
